import {WishlistState} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/wishlist.types';

type QueueItem<T> = {
  mutation: (response: T | undefined) => Promise<T | undefined>;
  onLastItem?: (response: Awaited<T>) => void;
  productId?: number;
  actionType: 'add' | 'remove';
};

export class WishlistQueue<T> {
  private queue: QueueItem<T>[] = [];
  private isRunning = false;
  private lastResponse: any = undefined;

  addToQueue({mutation, onLastItem, productId, actionType}: QueueItem<T>) {
    // Determine the opposite action type.
    const oppositeAction = actionType === 'add' ? 'remove' : 'add';

    // Find any queued actions for the same product but with the opposite action type.
    const conflictingActions = this.queue.filter(
      item =>
        item.productId === productId && item.actionType === oppositeAction,
    );

    // If there are any conflicting actions, remove them from the queue and return early.
    // conflicting means: If we want to queue an add but there is a remove queued.
    // we can then go ahead and remove the queued item and also not add this "add" item
    if (conflictingActions.length > 0) {
      this.queue = this.queue.filter(
        item =>
          !(item.productId === productId && item.actionType === oppositeAction),
      );
      return;
    }

    // Remove any existing actions for the same product and action type to avoid duplicates.
    this.queue = this.queue.filter(
      item => !(item.productId === productId && item.actionType === actionType),
    );

    // Add the new action to the queue.
    this.queue.push({mutation, onLastItem, productId, actionType});

    // Start processing the queue if it is not already running.
    if (!this.isRunning) {
      this.run();
    }
  }

  /**
   * Processes the queue, executing mutations sequentially and invoking the `onLastItem` callback if provided.
   */
  private async run() {
    this.isRunning = true;
    while (this.queue.length > 0) {
      const queueItem = this.queue.shift();
      if (queueItem) {
        // Execute the mutation, passing in the last response as context if available.
        const response = await queueItem.mutation(this.lastResponse);

        // If this is the last item in the queue and an `onLastItem` callback is provided, invoke it.
        if (this.queue.length === 0 && queueItem.onLastItem && response) {
          queueItem.onLastItem(response);
        }
        this.lastResponse = response;
      }
    }

    // Reset the last response and the running flag after processing the queue.
    this.lastResponse = undefined;
    this.isRunning = false;
  }

  get length() {
    return this.queue.length;
  }
}

export const MUTATION_QUEUE = new WishlistQueue<WishlistState>();
